import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Tabs from '@/components/Tabs.jsx';
import SeasonArchive from './history/SeasonArchive.jsx';
import PlayerArchiveTab from './history/PlayerArchiveTab.jsx';
import TopSeasonsTab from './history/TopSeasonsTab.jsx';
import {
	DEFAULT_SEASON_TAB,
	PRIMARY,
	historyPath,
	parseHistoryPath,
} from './history/tabRoutes.js';
import './HistoryPage.css';

/*
 * Two levels of tabs, matching the AngularJS page: Season Archive holds
 * everything scoped to a single season and owns the season selector, while
 * Player Archive and Top Seasons span every season.
 *
 * Tab and season both live in the path, so any view can be linked to and
 * counted separately in analytics. See tabRoutes.js for the URL shape.
 */
export default function HistoryPage() {
	const params = useParams();
	const navigate = useNavigate();
	const { primary, season, seasonTab } = parseHistoryPath(params['*']);

	/* Paths need a season to be well-formed, and the Player Archive and Top
	 * Seasons URLs do not carry one. Remembering the last season and tab
	 * seen means leaving Season Archive and coming back returns you to where
	 * you were rather than to the default. */
	const [last, setLast] = useState({ season: null, tab: DEFAULT_SEASON_TAB });

	useEffect(() => {
		if (!season && !seasonTab) return;
		setLast((prev) => ({
			season: season ?? prev.season,
			tab: seasonTab ?? prev.tab,
		}));
	}, [season, seasonTab]);

	const effectiveSeason = season ?? last.season;
	const effectiveTab = seasonTab ?? last.tab;

	const go = (next, options) =>
		navigate(
			historyPath({ primary, season: effectiveSeason, seasonTab: effectiveTab, ...next }),
			options,
		);

	const tabs = [
		{
			id: PRIMARY.SEASON,
			label: 'Season Archive',
			render: () => (
				<SeasonArchive
					season={effectiveSeason}
					onResolveSeason={(s) => setLast((prev) => ({ ...prev, season: prev.season ?? s }))}
					activeTab={effectiveTab}
					onSelectTab={(id) => go({ primary: PRIMARY.SEASON, seasonTab: id })}
					onSelectSeason={(next, options) =>
						go({ primary: PRIMARY.SEASON, season: next }, options)
					}
				/>
			),
		},
		{ id: PRIMARY.PLAYER, label: 'Player Archive', render: () => <PlayerArchiveTab /> },
		{ id: PRIMARY.TOP, label: 'Top Seasons', render: () => <TopSeasonsTab /> },
	];

	return (
		<div className="history-page">
			<header className="history-page__intro">
				<h1>FPL Points Archive</h1>
				<p className="history-page__lede">
					Season archives, player records and all-time leaderboards.
				</p>
			</header>

			<Tabs tabs={tabs} activeId={primary} onChange={(id) => go({ primary: id })} />
		</div>
	);
}
